"use client"

// Depense.js

import { useState, useEffect } from "react"
import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
  Typography,
  InputLabel,
  Select,
  MenuItem,
  FormControl,
  IconButton,
  CircularProgress,
  InputAdornment,
  alpha,
} from "@mui/material"
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  AttachMoney,
  Store as StoreIcon,
  Description as DescriptionIcon,
  CalendarToday,
} from "@mui/icons-material"
import { DataGrid, GridToolbar } from "@mui/x-data-grid"
import { useSelector, useDispatch } from "react-redux"
import {
  fetchDepenses,
  createDepense,
  updateDepense,
  deleteDepense,
  fetchDailyDepenses,
  fetchMonthlyDepenses,
  fetchDailyTotalDepenses,
  fetchMonthlyTotalDepenses,
} from "../../redux/actions/depense"
import { fetchAllShops } from "../../redux/actions/shop"
import { toast } from "react-toastify"
import Header from "../../components/Header"
import { tokens } from "../../theme"
import { useTheme } from "@mui/material"
import moment from "moment"
import "moment/locale/fr"
moment.locale("fr")

const formatCurrency = (amount) => {
  if (!amount || isNaN(amount)) return "0 BIF"
  return (
    new Intl.NumberFormat("fr-BI", {
      style: "decimal",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount) + " BIF"
  )
}

const Depense = () => {
  const theme = useTheme()
  const colors = tokens(theme.palette.mode)
  const dispatch = useDispatch()
  const { user } = useSelector((state) => state.user)
  const { depenses, dailyDepenses, monthlyDepenses, dailyTotalDepenses, monthlyTotalDepenses, loading, error } =
    useSelector((state) => state.depense)
  const { shops } = useSelector((state) => state.shop)

  const [openAdd, setOpenAdd] = useState(false)
  const [openEdit, setOpenEdit] = useState(false)
  const [selectedDepense, setSelectedDepense] = useState(null)
  const [depenseData, setDepenseData] = useState({
    description: "",
    amount: "",
  })
  const [filterShopId, setFilterShopId] = useState("")
  const [isSubmitting, setIsSubmitting] = useState(false)

  const fetchDataForShop = async (shopId) => {
    try {
      // If shopId is 'all', don't pass any shopId to fetch all depenses
      const shopIdParam = shopId === "all" ? undefined : shopId

      await Promise.all(
        [
          dispatch(fetchDepenses(shopIdParam)),
          shopIdParam && dispatch(fetchDailyDepenses(shopIdParam)),
          shopIdParam && dispatch(fetchMonthlyDepenses(shopIdParam)),
          dispatch(fetchDailyTotalDepenses()),
          dispatch(fetchMonthlyTotalDepenses()),
        ].filter(Boolean),
      ) // Remove any falsy values from the array
    } catch (error) {
      console.error("Error fetching shop data:", error)
      if (error !== "User does not have a shop assigned.") {
        toast.error("Failed to load expenses data")
      }
    }
  }

  const loadInitialData = async () => {
    try {
      if (user) {
        if (user.role === "superadmin" || user.role === "supervisor") {
          await dispatch(fetchAllShops())
          const firstShop = shops[0]
          if (firstShop) {
            setFilterShopId(firstShop._id)
            await fetchDataForShop(firstShop._id)
          }
        } else {
          const shopId = typeof user.shop === "object" ? user.shop._id : user.shop
          setFilterShopId(shopId)
          await fetchDataForShop(shopId)
        }
      }
    } catch (error) {
      console.error("Error loading expenses:", error)
      toast.error("Failed to load expenses")
    }
  }

  // This effect is now handled by loadInitialData

  useEffect(() => {
    loadInitialData()
  }, [dispatch, user, shops.length])

  useEffect(() => {
    if (error) {
      toast.error(error)
    }
  }, [error])

  const handleAddOpen = () => {
    setDepenseData({
      description: "",
      amount: "",
    })
    setOpenAdd(true)
  }

  const handleAddClose = () => {
    setOpenAdd(false)
  }

  const handleEditClose = () => {
    setOpenEdit(false)
  }

  const handleChange = (e) => {
    const { name, value } = e.target
    setDepenseData((prevData) => ({
      ...prevData,
      [name]: value,
    }))
  }

  const handleAddSubmit = () => {
    if (!depenseData.description || !depenseData.amount) {
      toast.error("Description and montant are required")
      return
    }

    // For supervisors and superadmins, use the selected shop ID if available
    // For regular users, use their assigned shop
    const shopId =
      user.role === "superadmin" || user.role === "supervisor"
        ? filterShopId === "all"
          ? undefined
          : filterShopId
        : user?.shop
          ? typeof user.shop === "object"
            ? user.shop._id
            : user.shop
          : undefined

    const submitData = {
      ...depenseData,
      shopId: shopId,
    }

    // If no shop is selected for admin/supervisor, show an error
    if ((user.role === "superadmin" || user.role === "supervisor") && !shopId) {
      toast.error("Please select a shop before adding an expense")
      return
    }

    setIsSubmitting(true)
    dispatch(createDepense(submitData))
      .then(() => {
        toast.success("Dépense created successfully")
        setOpenAdd(false)
        setIsSubmitting(false)
        refreshData(shopId)
      })
      .catch((error) => {
        toast.error(error)
        setIsSubmitting(false)
      })
  }

  const handleFilterChange = (e) => {
    const selectedShopId = e.target.value
    setFilterShopId(selectedShopId)
    refreshData(selectedShopId)
  }

  const refreshData = (shopId) => {
    let id = shopId

    if (!id) {
      if (user?.role === "superadmin" || user?.role === "supervisor") {
        // For admins and supervisors, use the first shop if available, or 'all' to show all shops
        id = shops.length > 0 ? shops[0]?._id : "all"
      } else if (user?.shop) {
        // For regular users, use their assigned shop
        id = typeof user.shop === "object" ? user.shop._id : user.shop
      }
    }

    if (id) {
      fetchDataForShop(id)
    }
  }

  const handleEditClick = (depense) => {
    setSelectedDepense(depense)
    setDepenseData({
      description: depense.description,
      amount: depense.amount,
    })
    setOpenEdit(true)
  }

  const handleEditSubmit = () => {
    if (!depenseData.description || !depenseData.amount) {
      toast.error("Description and montant are required")
      return
    }
    setIsSubmitting(true)
    dispatch(updateDepense(selectedDepense._id, depenseData))
      .then(() => {
        toast.success("Depense updated successfully")
        setOpenEdit(false)
        setIsSubmitting(false)
        refreshData()
      })
      .catch((error) => {
        toast.error(error)
        setIsSubmitting(false)
      })
  }

  const handleDelete = (id) => {
    if (window.confirm("Are you sure you want to delete this depense?")) {
      dispatch(deleteDepense(id))
        .then(() => {
          toast.success("Depense deleted successfully")
          refreshData()
        })
        .catch((error) => {
          toast.error(error)
        })
    }
  }

  const columns = [
    {
      field: "createdAt",
      headerName: "Date",
      flex: 1,
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <CalendarToday sx={{ fontSize: 20, color: colors.primary[600] }} />
          {moment(params.row.createdAt).format("DD/MM/YYYY HH:mm")}
        </Box>
      ),
    },
    {
      field: "description",
      headerName: "Description",
      flex: 2,
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <DescriptionIcon sx={{ fontSize: 20, color: colors.primary[600] }} />
          {params.row.description}
        </Box>
      ),
    },
    {
      field: "amount",
      headerName: "Montant",
      flex: 1,
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, fontWeight: 500 }}>
          {formatCurrency(params.row.amount)}
        </Box>
      ),
    },
    {
      field: "shop",
      headerName: "Boutique",
      flex: 1,
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <StoreIcon sx={{ fontSize: 20, color: colors.primary[600] }} />
          {params.row.shop?.name || "N/A"}
        </Box>
      ),
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 1,
      sortable: false,
      renderCell: (params) => (
        <Box sx={{ display: "flex", gap: 1 }}>
          {user.role !== "supervisor" && (
            <>
              <IconButton
                onClick={() => handleEditClick(params.row)}
                size="small"
                sx={{
                  "&:hover": { color: colors.greenAccent[400], backgroundColor: "#e8f5e9" },
                }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
              <IconButton
                onClick={() => handleDelete(params.row._id)}
                size="small"
                sx={{
                  "&:hover": { color: colors.redAccent[400], backgroundColor: "#ffebee" },
                }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </>
          )}
        </Box>
      ),
    },
  ]

  // Calculate total amounts safely
  const totalDailyAmount =
    user.role === "superadmin" || user.role === "supervisor"
      ? filterShopId
        ? dailyDepenses?.totalAmount || 0
        : Object.values(dailyTotalDepenses || {}).reduce((a, b) => a + b, 0)
      : dailyDepenses?.totalAmount || 0

  const totalMonthlyAmount =
    user.role === "superadmin" || user.role === "supervisor"
      ? filterShopId
        ? monthlyDepenses?.totalAmount || 0
        : Object.values(monthlyTotalDepenses || {}).reduce((a, b) => a + b, 0)
      : monthlyDepenses?.totalAmount || 0

  return (
    <Box m="20px">
      <Header title="DÉPENSES" subtitle="Gestion des dépenses" />

      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid item xs={12} md={6}>
          <Card
            sx={{
              backgroundColor: "primary.main",
              color: "white",
            }}
          >
            <CardContent>
              <Typography variant="h6" sx={{ opacity: 0.9 }}>
                Dépenses Mensuelles
              </Typography>
              <Typography variant="h3" sx={{ fontWeight: "bold", mt: 1, color: "white" }}>
                {formatCurrency(totalMonthlyAmount)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} flexWrap="wrap" gap={2}>
        <Box flexGrow={1}>
          {(user?.role === "superadmin" || user?.role === "supervisor") && (
            <FormControl sx={{ minWidth: 220 }} size="small">
              <InputLabel>Filtrer par boutique</InputLabel>
              <Select value={filterShopId || "all"} onChange={handleFilterChange} label="Filtrer par boutique">
                <MenuItem value="all">Toutes les boutiques</MenuItem>
                {shops.map((shop) => (
                  <MenuItem key={shop._id} value={shop._id}>
                    {shop.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </Box>
        {user.role !== "supervisor" && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddOpen}
            sx={{
              backgroundColor: "primary.main",
              "&:hover": {
                backgroundColor: "primary.dark",
                boxShadow: `0 8px 24px rgba(34, 197, 94, 0.4)`,
                transform: "translateY(-2px)",
              },
            }}
          >
            Nouvelle Dépense
          </Button>
        )}
      </Box>

      <Box
        height="60vh"
        sx={{
          "& .MuiDataGrid-root": {
            border: "none",
            backgroundColor: "background.paper",
            borderRadius: 2,
            overflow: "hidden",
          },
          "& .MuiDataGrid-cell": {
            borderBottom: `1px solid ${alpha("#000", 0.1)}`,
          },
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: "background.neutral",
            borderBottom: "none",
          },
          "& .MuiDataGrid-footerContainer": {
            borderTop: "none",
            backgroundColor: "background.neutral",
          },
          "& .MuiDataGrid-row:hover": {
            backgroundColor: alpha("#000", 0.04),
          },
          "& .MuiDataGrid-toolbarContainer .MuiButton-text": {
            color: `${colors.grey[100]} !important`,
          },
        }}
      >
        <DataGrid
          rows={depenses || []}
          columns={columns}
          components={{
            Toolbar: () => <GridToolbar />,
            LoadingOverlay: () => (
              <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                <CircularProgress sx={{ color: colors.primary[600] }} />
              </Box>
            ),
          }}
          loading={loading}
          getRowId={(row) => row._id}
        />
      </Box>

      <Dialog
        open={openAdd || openEdit}
        onClose={() => (openAdd ? handleAddClose() : handleEditClose())}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: "0 20px 60px rgba(0, 0, 0, 0.15)",
            backgroundColor: theme.palette.background.paper,
          },
        }}
      >
        <DialogTitle
          sx={{
            fontWeight: 600,
            backgroundColor: theme.palette.mode === "dark" ? "primary.dark" : "primary.light",
            borderBottom: `2px solid primary.main`,
            color: "text.primary",
            mb: 2,
          }}
        >
          {openAdd ? "Nouvelle Dépense" : "Modifier la Dépense"}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                name="description"
                value={depenseData.description}
                onChange={handleChange}
                required
                sx={{
                  mb: 2,
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                    "& fieldset": { borderColor: colors.grey[300] },
                    "&:hover fieldset": { borderColor: colors.primary[600] },
                    "&.Mui-focused fieldset": { borderColor: colors.primary[600] },
                  },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <DescriptionIcon sx={{ color: colors.primary[600] }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Montant"
                name="amount"
                type="number"
                value={depenseData.amount}
                onChange={handleChange}
                required
                sx={{
                  mb: 2,
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                    "& fieldset": { borderColor: colors.grey[300] },
                    "&:hover fieldset": { borderColor: colors.primary[600] },
                    "&.Mui-focused fieldset": { borderColor: colors.primary[600] },
                  },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <AttachMoney sx={{ color: colors.primary[600] }} />
                    </InputAdornment>
                  ),
                  endAdornment: <InputAdornment position="end">BIF</InputAdornment>,
                }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ borderTop: `1px solid ${colors.grey[300]}`, p: 2 }}>
          <Button
            onClick={() => (openAdd ? handleAddClose() : handleEditClose())}
            sx={{
              color: "grey.500",
              "&:hover": { color: "grey.900" },
            }}
          >
            Annuler
          </Button>
          <Button
            onClick={() => (openAdd ? handleAddSubmit() : handleEditSubmit())}
            variant="contained"
            disabled={isSubmitting}
            sx={{
              backgroundColor: "primary.main",
              "&:hover": {
                backgroundColor: "primary.dark",
                boxShadow: `0 8px 24px rgba(34, 197, 94, 0.4)`,
              },
              "&.Mui-disabled": {
                background: "grey.400",
              },
            }}
          >
            {isSubmitting ? <CircularProgress size={24} sx={{ color: "white" }} /> : openAdd ? "Ajouter" : "Modifier"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default Depense
